import { DataGrid } from '@tableau/widgets-datagrid';
import React from 'react';
import { defaultMemoize } from 'reselect';

import { Filter } from '~commonInfra/utils/Filter';
import { CheckboxState } from '~src/commonInfra/components/checkbox/CheckboxState';
import { ReactCheckboxTristate } from '~src/commonInfra/components/checkbox/ReactCheckboxTristate';
import { createRowStore, defaultRowStructure } from '~src/commonInfra/components/dataGrid/SortableDataGrid3';
import { TypeUnsafeTranslation } from '~src/commonInfra/services/IReactTranslateService';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';

import { Messages } from '../../codegen/Localize';
import { ListResult } from '../../httpClient/fetchers/ListResult';
import { SliceableHelper } from '../../httpClient/utils/SliceableHelper';
import { ISiteGridAdminProfileCellProps,SiteMembershipGridColumns } from '../../siteManagement/components/SiteMembershipGridColumns';
import { ISiteNameWithId } from '../../siteManagement/serverApi/types/ISiteNameWithId';
import { SiteNameWithIds } from '../../siteManagement/services/resources/SiteNameWithIds';
import { ADMIN_PROFILE_NONE_VALUE, isAdminProfileChoiceEnabled } from '../helpers/AdminProfileConstants';
import {
  getAdminProfileListItemsForSiteMembershipGrid,
  SITE_MEMBERSHIP_GRID_DROPDOWN_WIDTH
} from '../helpers/SiteMembershipGridAdminProfileHelper';
import { ServerUsers } from '../services/resources/ServerUsers';
import { DefaultSiteRole, SiteRoleOption, SiteRoleOptionsResource } from '../services/resources/SiteRoleOptions';
import { ISiteRoleChange } from '../services/SiteRolesPickerClientService';
import { SiteRoles } from '../users/serverApi/types/ISiteRole';
import { UserRoleSelections } from './EditSiteMembershipDialog';
import { SiteRolesPickerAdminCheckboxHelper } from './helpers/SiteRolesPickerAdminCheckboxHelper';
import { SiteRolesPickerDataGridHelper } from './helpers/SiteRolesPickerDataGridHelper';
import { SiteRolesPickerFiltersHelper } from './helpers/SiteRolesPickerFiltersHelper';
import { SiteMembershipFilters } from './SiteMembershipFilters';
import { SiteRoleInfoLink } from './SiteRoleInfoLink';
import { SiteRolePickerMenu } from './siteRolePicker/SiteRolePickerMenu';
import { SiteRolesPickerContainerEmptyTemplate } from './SiteRolesPickerContainerEmptyTemplate';

export interface SiteRolesPickerContainerState {
    // TODO: 801446, siteNames and siteNamesWithFilter to be collapsed into one
    siteNames: ISiteNameWithId[];
    siteNamesWithFilter: ISiteNameWithId[];
    siteRowCheckboxPropMap: SiteRoleCheckboxPropsDictionary;
    siteRowDropdownPropMap: SiteRoleSelectorPropsDictionary;
    /** Admin profile per site. Key: siteId (e.g. "1"). Value: profile LUID or ADMIN_PROFILE_NONE_VALUE ("none"). */
    siteRowAdminProfileMap: Record<string, string>;
    /** Admin profiles per site (loaded when dropdown opens for that site). */
    siteAdminProfilesMap: Record<string, Array<{ luid: string, name: string }>>;
    /** Site ids for which admin profiles are currently loading. */
    siteAdminProfilesLoading: Record<string, boolean>;
    availableSiteFilters: string[];
    currentSiteFilter: string;
    siteSearchQuery: string;
    adminCheckboxState: CheckboxState;

    //TODO: 801955, take these backups out of the state and put as class member
    siteRowCheckboxPropMapBackup: SiteRoleCheckboxPropsDictionary;
    siteRowDropdownPropMapBackup: SiteRoleSelectorPropsDictionary;
    lastSiteFilter: string;
}

export interface SiteRolesPickerContainerProps {
    changes: (siteRoles: ISiteRoleChanges) => void;
    busy: boolean;
    userRoleSelections?: UserRoleSelections;
}

export interface ISiteRoleChanges {
    siteRoles: _.Dictionary<ISiteRoleChange>; // changes keyed by siteId
    serverAdmin: string; // CheckboxState
}

export interface SiteRoleSelectorPropsDictionary {
    [siteId: string]: SiteRolePickerMenu.Props;
}

export interface SiteRoleCheckboxPropsDictionary {
    [siteId: string]: ReactCheckboxTristate.Props;
}

export let SiteMembershipDialogFilters = {
    AllSites: 'siteMembershipAllSites',
    AssignedSites: 'siteMembershipAssignedSites',
    SearchResults: 'siteMembershipSearchResults'
};

export interface IUserCountsAndRolesBySiteId extends _.Dictionary<{
    count: number;
    siteRole: string;
    /** Admin profile LUID when all selected users on this site have the same profile; otherwise undefined or 'none'. */
    adminProfile?: string;
}> { }

/** Column dataKey for site role dropdown; used to avoid toggling row when clicking that cell. */
const SITE_ROLE_COLUMN_INDEX = '2';
/** Column dataKey for admin profile dropdown when feature is on; used to avoid toggling row when clicking that cell. */
const ADMIN_PROFILE_COLUMN_INDEX = '3';

const DefaultDataGridConfig: DataGrid.Config = {
    minimumColumnWidth: 38, // this is the smallest size we want the selection column to be
    maxInitialColumnWidth: -1 // negative number removes maximum width so takes up column width specified
};

const SiteRoleInfoLinkContainerStyle: React.CSSProperties = { marginBottom: '7px' };

export class SiteRolesPickerContainer extends React.Component<SiteRolesPickerContainerProps, SiteRolesPickerContainerState> {

    private DefaultCheckboxProp: ReactCheckboxTristate.Props = {
        checkboxState: CheckboxState.empty,
        clicked: () => { },
        disabled: false
    };

    private createDefaultDropdownProp(site: ISiteNameWithId): SiteRolePickerMenu.Props {
        return {
            siteRoleOptions: this.siteRolesResource.getAllSiteRoleOptionsExcept(SiteRoles.ServerAdministrator),
            getSiteRoleOptionBySiteRole: (siteRole: string) => this.siteRolesResource.getSiteRoleOptionBySiteRole(siteRole),
            onOptionSelected: (siteRoleOption: SiteRoleOption) => this.onSiteRoleSelected(site, siteRoleOption),
            selectedRole: this.siteRolesResource.getSiteRoleOptionBySiteRole(DefaultSiteRole),
            isDisabled: false,
            style: { width: SITE_MEMBERSHIP_GRID_DROPDOWN_WIDTH },
        };
    }

    private rowStoreWrapper = defaultMemoize((siteNamesWithFilter: ISiteNameWithId[]): DataGrid.RowStore => createRowStore(() => SliceableHelper.fromArray(siteNamesWithFilter), defaultRowStructure, []));

    private getInitialStateFromSiteNames(siteNames: ISiteNameWithId[]): SiteRolesPickerContainerState {
        let siteRowDropdownPropMap: SiteRoleSelectorPropsDictionary = {};
        let siteRowCheckboxPropMap: SiteRoleCheckboxPropsDictionary = {};
        let siteRowAdminProfileMap: Record<string, string> = {};
        for (let site of siteNames) {
            siteRowDropdownPropMap[site.id] = this.createDefaultDropdownProp(site);
            siteRowCheckboxPropMap[site.id] = this.DefaultCheckboxProp;
            siteRowAdminProfileMap[site.id] = ADMIN_PROFILE_NONE_VALUE;
        }
        return { ...this.state, siteRowDropdownPropMap, siteRowCheckboxPropMap, siteRowAdminProfileMap };
    }

    private siteRolesResource: SiteRoleOptionsResource = SiteRoleOptionsResource.instance;

    private columnWrapper = defaultMemoize((
        siteToCheckboxPropMap: SiteRoleCheckboxPropsDictionary,
        siteToDropdownPropMap: SiteRoleSelectorPropsDictionary,
        siteRowAdminProfileMap: Record<string, string>,
        _siteAdminProfilesMap: Record<string, Array<{ luid: string, name: string }>>
    ) => this.buildColumns(siteRowAdminProfileMap, _siteAdminProfilesMap));
    private unmounted: boolean = false;

    private buildColumns(siteRowAdminProfileMap: Record<string, string>, _siteAdminProfilesMap: Record<string, Array<{ luid: string, name: string }>>) {
    const adminProfileProps = getSessionManagementServiceInstance().isAdminProfilesEnabled()
      ? (site: ISiteNameWithId): ISiteGridAdminProfileCellProps | null => this.getPropsForAdminProfileCell(site)
      : undefined;
        const cols = SiteMembershipGridColumns.columns(
            this.getPropsForDropdownCell.bind(this),
            this.getPropsForCheckboxCell.bind(this),
            adminProfileProps
        );
        return cols.map((c, i) => ({ ...c }));
    }

    public constructor(props: SiteRolesPickerContainerProps) {
        super(props);
        this.state = {
            siteNames: [],
            siteNamesWithFilter: [],
            lastSiteFilter: SiteMembershipDialogFilters.AllSites,
            siteRowCheckboxPropMapBackup: null,
            siteRowDropdownPropMapBackup: null,
            siteRowAdminProfileMap: {},
            siteAdminProfilesMap: {},
            siteAdminProfilesLoading: {},
            availableSiteFilters: [SiteMembershipDialogFilters.AllSites, SiteMembershipDialogFilters.AssignedSites],
            currentSiteFilter: SiteMembershipDialogFilters.AllSites,
            siteSearchQuery: '',
            adminCheckboxState: CheckboxState.empty,
            siteRowCheckboxPropMap: {},
            siteRowDropdownPropMap: {}
        };
        SliceableHelper.fetchAll(SiteNameWithIds.instance.all()).then((siteNames: ListResult<ISiteNameWithId>) => {
            if (!this.unmounted) {
                // W-15023839: When there are enough sites to be sent in batches, the user role information can be available before
                // the full list of sites gets processed above. Compute initial state from the freshly loaded sites, then apply
                // any already-available role selections in a single setState to avoid a second setState overwriting the first.
                let newState: SiteRolesPickerContainerState = {
                    ...this.getInitialStateFromSiteNames(siteNames.items),
                    siteNamesWithFilter: siteNames.items,
                    siteNames: siteNames.items
                };
                if (this.props && this.props.userRoleSelections) {
                    newState = { ...newState, ...SiteRolesPickerDataGridHelper.getStateOnModifyRoles(newState, this.props.userRoleSelections, this.siteRolesResource) };
                }
                this.setState(newState);
            }
        });
    }

    // FIXME: Either the component should not manage its own state or its state should not depend on its props.
    // See: https://reactjs.org/blog/2018/06/07/you-probably-dont-need-derived-state.html
    public componentDidUpdate(prevProps: SiteRolesPickerContainerProps) {
        let state = this.state;
        if (this.props.busy !== prevProps.busy) {
            state = { ...state, ...SiteRolesPickerDataGridHelper.getStateOnBusyChange(this.state, this.props.busy) };
        }
        if (this.props.userRoleSelections !== prevProps.userRoleSelections) {
            state = { ...state, ...SiteRolesPickerDataGridHelper.getStateOnModifyRoles(this.state, this.props.userRoleSelections, this.siteRolesResource) };
        }
        if (state !== this.state) {
            if (!this.unmounted) {
                this.setState(state);
            }
        }
        this.ensureAdminProfilesLoadedForSites();
    }

    /** Triggers loading of admin profiles for sites that need them. Called from componentDidUpdate (after render) to avoid setState-during-render loop. */
    private ensureAdminProfilesLoadedForSites(): void {
        if (!getSessionManagementServiceInstance().isAdminProfilesEnabled() || this.props.busy) {
            return;
        }
        const { siteNamesWithFilter, siteRowDropdownPropMap, siteRowCheckboxPropMap, siteAdminProfilesMap, siteAdminProfilesLoading } = this.state;
        for (const site of siteNamesWithFilter) {
            // Only load admin profiles for sites the user has selected — the admin profile cell is not
            // rendered for unchecked sites, so fetching data for them is wasteful.
            const checkboxState = siteRowCheckboxPropMap[site.id]?.checkboxState;
            if (checkboxState === CheckboxState.empty) {
                continue;
            }
            const selectedRole = siteRowDropdownPropMap[site.id]?.selectedRole;
            const siteRole = selectedRole?.role;
            const enabled = !!siteRole && isAdminProfileChoiceEnabled(siteRole);
            const alreadyLoaded = siteAdminProfilesMap[site.id] !== undefined;
            const isLoading = !!siteAdminProfilesLoading[site.id];
            if (enabled && !alreadyLoaded && !isLoading) {
                this.loadAdminProfilesForSite(site.id);
            }
        }
    }

    public componentWillUnmount() {
        this.unmounted = true;
    }

    private readonly selectAllSiteFilter = () => {
        this.filterSelectedHandler(SiteMembershipDialogFilters.AllSites);
    };

    private readonly filterSelectedHandlerCallback = (value: string) => {
        this.filterSelectedHandler(value);
    };

    private readonly onSearchInputTextChangeCallback = (searchInput: string) => {
        this.onSearchInputTextChange(searchInput);
    };

    private readonly onCancelSearchCallback = () => {
        this.onCancelSearch();
    };

    private readonly onCellClickCallback: DataGrid.ClickHandler = (row, cell) => {
        this.onCellClicked(row, String(cell.colIdx));
    };

    private readonly adminCheckboxClickedCallback = () => {
        this.adminCheckboxClicked();
    };

    public render() {
        return (
            <div className="tb-fill tb-site-roles-picker" tb-test-id="picker-control">
                <SiteMembershipFilters filters={this.state.availableSiteFilters}
                    onFilterSelected={this.filterSelectedHandlerCallback}
                    siteFilterSelection={this.state.currentSiteFilter}
                    onCancelSearch={this.onCancelSearchCallback}
                    onSearchInputTextChange={this.onSearchInputTextChangeCallback}
                    searchInputText={this.state.siteSearchQuery}
                    translate={TypeUnsafeTranslation.translate} />
                <div className={'tb-bottom tb-grid-container'}>
                    <DataGrid emptyChild={<SiteRolesPickerContainerEmptyTemplate actionHandler={this.selectAllSiteFilter}
                        title={this.getEmptyTemplateModelTitle()}
                        actionTitle={TypeUnsafeTranslation.translate('assignSites')}
                        siteNames={this.state.siteNames} />}
                        cols={this.columnWrapper(this.state.siteRowCheckboxPropMap, this.state.siteRowDropdownPropMap, this.state.siteRowAdminProfileMap, this.state.siteAdminProfilesMap)}
                        config={DefaultDataGridConfig}
                        showRowBanding={false}
                        rowStore={this.rowStoreWrapper(this.state.siteNamesWithFilter)}
                        showHorizontalGridLines={false}
                        onCellClick={this.onCellClickCallback} />
                </div>
                <div className={'tb-admin-checkbox'}>
                    <div style={SiteRoleInfoLinkContainerStyle}>
                        <SiteRoleInfoLink />
                    </div>
                    <ReactCheckboxTristate checkboxState={this.state.adminCheckboxState}
                        clicked={this.adminCheckboxClickedCallback}
                        disabled={this.props.busy}
                        label={Messages.serverAdminSelect()} />
                </div>
            </div>
        );
    }

    private getPropsForCheckboxCell(site: ISiteNameWithId): ReactCheckboxTristate.Props {
        if (!this.state.siteRowCheckboxPropMap[site.id]) {
            return this.DefaultCheckboxProp;
        }
        return this.state.siteRowCheckboxPropMap[site.id];
    }


    private getPropsForDropdownCell(site: ISiteNameWithId): SiteRolePickerMenu.Props {
        if (!this.state.siteRowDropdownPropMap[site.id]) {
            return this.createDefaultDropdownProp(site);
        }
        return this.state.siteRowDropdownPropMap[site.id];
    }

    private loadAdminProfilesForSite(siteId: string): void {
        const siteIdNum = parseInt(siteId, 10);
        if (Number.isNaN(siteIdNum) || siteIdNum <= 0 || this.state.siteAdminProfilesLoading[siteId]) {
            return;
        }
        this.setState({
            siteAdminProfilesLoading: { ...this.state.siteAdminProfilesLoading, [siteId]: true }
        });
        ServerUsers.instance.getAdminProfilesForSite(siteIdNum)
            .then((result) => {
                if (!this.unmounted && result) {
                    const profiles = (result.adminProfiles || []).map((p) => ({ luid: p.luid, name: p.name }));
                    this.setState({
                        siteAdminProfilesMap: { ...this.state.siteAdminProfilesMap, [siteId]: profiles },
                        siteAdminProfilesLoading: { ...this.state.siteAdminProfilesLoading, [siteId]: false }
                    });
                }
            })
            .catch(() => {
                if (!this.unmounted) {
                    this.setState({
                        siteAdminProfilesLoading: { ...this.state.siteAdminProfilesLoading, [siteId]: false }
                    });
                }
            });
    }

    private getPropsForAdminProfileCell(site: ISiteNameWithId): ISiteGridAdminProfileCellProps | null {
        if (!getSessionManagementServiceInstance().isAdminProfilesEnabled()) {
            return null;
        }
        const selectedRole = this.state.siteRowDropdownPropMap[site.id]?.selectedRole;
        const siteRole = selectedRole?.role;
        if (!siteRole) {
            return null;
        }
        const enabled = isAdminProfileChoiceEnabled(siteRole);
        if (!enabled) {
            return {
                value: ADMIN_PROFILE_NONE_VALUE,
                onChange: () => {},
                disabled: true,
                listItems: getAdminProfileListItemsForSiteMembershipGrid([], ADMIN_PROFILE_NONE_VALUE)
            };
        }
        const profilesForSite = this.state.siteAdminProfilesMap[site.id];
        if (profilesForSite === undefined) {
            return null;
        }
        return {
            value: this.state.siteRowAdminProfileMap[site.id],
            onChange: (newValue: string) => this.onAdminProfileSelected(site, newValue),
            disabled: this.props.busy,
            listItems: getAdminProfileListItemsForSiteMembershipGrid(profilesForSite, ADMIN_PROFILE_NONE_VALUE)
        };
    }

    private onAdminProfileSelected(site: ISiteNameWithId, adminProfile: string): void {
        const newMap = { ...this.state.siteRowAdminProfileMap, [site.id]: adminProfile };
        const newState = { ...this.state, siteRowAdminProfileMap: newMap };
        this.props.changes(this.getSiteRoleChanges(newState));
        if (!this.unmounted) {
            this.setState({ siteRowAdminProfileMap: newMap });
        }
    }

    private onSiteRoleSelected(site: ISiteNameWithId, siteRoleOption: SiteRoleOption): void {
        let newState = SiteRolesPickerDataGridHelper.getStateOnSiteRoleSelected(this.state, site, siteRoleOption, this.siteRolesResource);
        this.props.changes(this.getSiteRoleChanges(newState));
        if (!this.unmounted) {
            this.setState(newState);
        }
    }

    private onCellClicked(row: any, columnIndex?: string) {
        const isSiteRoleOrAdminProfileCol = columnIndex === SITE_ROLE_COLUMN_INDEX || columnIndex === ADMIN_PROFILE_COLUMN_INDEX;
        if ((!isSiteRoleOrAdminProfileCol || this.state.siteRowCheckboxPropMap[row.id].checkboxState !== CheckboxState.checked) &&
            this.state.adminCheckboxState !== CheckboxState.checked && !this.props.busy && this.state.adminCheckboxState !== CheckboxState.mixed) {
            let newState: SiteRolesPickerContainerState = SiteRolesPickerDataGridHelper.getStateOnCellClicked(this.state, row, this.siteRolesResource);
            this.props.changes(this.getSiteRoleChanges(newState));
            if (!this.unmounted) {
                this.setState(newState);
            }
        }
    }

    private adminCheckboxClicked() {
        let adminCheckboxState = CheckboxState.toggle(this.state.adminCheckboxState as string);
        let newState: SiteRolesPickerContainerState;

        if (adminCheckboxState === CheckboxState.checked) {
            newState = SiteRolesPickerAdminCheckboxHelper.getStateWhenChecked(this.state, this.siteRolesResource, this.props.userRoleSelections);
            this.props.changes(this.getSiteRoleChanges(newState));
            this.setStateThenFilter(newState, this.state.currentSiteFilter);
        } else {
            newState = SiteRolesPickerAdminCheckboxHelper.getStateWhenEmpty(this.state, this.props.userRoleSelections, this.siteRolesResource);
            this.props.changes(this.getSiteRoleChanges(newState));
            this.setStateThenFilter(newState, this.state.currentSiteFilter);
        }
    }

    /** Builds change payload for parent. Uses newState.siteRowAdminProfileMap so latest admin profile choice is included. */
    public getSiteRoleChanges(newState: SiteRolesPickerContainerState): ISiteRoleChanges {
        const siteRolesPickerChanges: ISiteRoleChanges = { siteRoles: {}, serverAdmin: undefined };

        if (newState.adminCheckboxState === CheckboxState.checked) {
            siteRolesPickerChanges.serverAdmin = CheckboxState.checked;
        } else {
            for (const site in newState.siteRowCheckboxPropMap) {
                const siteRole = newState.siteRowDropdownPropMap[site].selectedRole;
                const checkboxState = newState.siteRowCheckboxPropMap[site].checkboxState as string;
                const originalCheckboxState = this.getOriginalCheckboxStateForSite(this.props.userRoleSelections, site);
                if (checkboxState === CheckboxState.checked || originalCheckboxState !== checkboxState) {
                    const siteNameWithId = this.state.siteNames.find(sn => sn.id === site);
                    const siteRoleChange: ISiteRoleChange = {
                        checkboxState,
                        siteRole: siteRole.role,
                        siteName: siteNameWithId ? siteNameWithId.name : ''
                    };
                    if (getSessionManagementServiceInstance().isAdminProfilesEnabled()) {
                        siteRoleChange.adminProfile = newState.siteRowAdminProfileMap[site] || ADMIN_PROFILE_NONE_VALUE;
                    }
                    siteRolesPickerChanges.siteRoles[site] = siteRoleChange;
                }
            }
            if (this.props.userRoleSelections && this.props.userRoleSelections.numSelectedServerAdmins > 0) {
                siteRolesPickerChanges.serverAdmin = newState.adminCheckboxState as string;
            }
        }
        return siteRolesPickerChanges;
    }

    // input a site ID, returns the initial checkbox state for the given site (checked, empty, or mixed)
    public getOriginalCheckboxStateForSite(userRoleSelections: UserRoleSelections, siteId: string): string {
        if (!userRoleSelections || !userRoleSelections.userCountsAndRolesBySiteId[siteId]) {
            return CheckboxState.empty;
        } else if (userRoleSelections.userCountsAndRolesBySiteId[siteId].count === userRoleSelections.numSelectedUsers) {
            return CheckboxState.checked;
        } else {
            return CheckboxState.mixed;
        }
    }

    private getEmptyTemplateModelTitle(): string {
        if (this.state.currentSiteFilter === SiteMembershipDialogFilters.SearchResults) {
            return Messages.noMatchingResults();
        }
        return Messages.noAssignedSites();
    }

    private onSearchInputTextChange(searchInput: string) {
        this.setStateThenFilter(SiteRolesPickerFiltersHelper.getStateOnTextChange(this.state, searchInput), SiteMembershipDialogFilters.SearchResults);
    }

    private onCancelSearch() {
        if (!this.unmounted) {
            this.setState(SiteRolesPickerFiltersHelper.getStateOnCancelSearch(this.state), () => {
                if (!this.unmounted) {
                    this.filterSelectedHandler(this.state.currentSiteFilter);
                }
            });
        }
    }

    private filterSelectedHandler(value: string) {
        switch (value) {
            case SiteMembershipDialogFilters.AllSites:
                if (!this.unmounted) {
                    this.setState(SiteRolesPickerFiltersHelper.getStateForAllSitesFilter(this.state));
                }
                return;
            case SiteMembershipDialogFilters.AssignedSites:
                if (!this.unmounted) {
                    this.setState(SiteRolesPickerFiltersHelper.getStateForAssignedSitesFilter(this.state));
                }
                return;
            case SiteMembershipDialogFilters.SearchResults:
                SliceableHelper.fetchAll(SiteNameWithIds.instance.all().withFilters([Filter.search(this.state.siteSearchQuery)])).then((searchResults: ListResult<ISiteNameWithId>) => {
                    if (!this.unmounted) {
                        this.setState(SiteRolesPickerFiltersHelper.getStateForSearchResultsFilter(this.state, searchResults.items));
                    }
                });
                return;
        }
    }

    private setStateThenFilter(state: SiteRolesPickerContainerState, value: string) {
        if (!this.unmounted) {
            this.setState(state, () => {
                if (!this.unmounted) {
                    this.filterSelectedHandler(value);
                }
            });
        }
    }
}
